import { WARM_AUDIENCE_ID } from '@/utils/warm-audiences'
import { isAccountAudienceId } from '@/utils/account-audiences'
import { 
    OPTIMAL_BUDGET_ALLOCATION,
    OBJECTIVE_AVAILABLE_STAGES,
    FUNNEL_STAGES,
    EXCLUDED_AUDIENCES,
    EXCLUSION_REASONS,
    type CampaignStage,
    type CampaignObjective,
    type BudgetAllocation,
    type FunnelStageConfig,
    type AudienceSelections,
    type Audience
} from './ad-campaign-constants'

/**
 * Given a base set of percentages and an overrides object,
 * redistributes the remaining percentage proportionally.
 *
 * @param base      – e.g. OPTIMAL_BUDGET_ALLOCATION[objective]
 * @param overrides – e.g. { retargeting: 25 }  (each ≤100)
 * @returns         – new pct mapping summing to 100
 */
function adjustPercentages(base: BudgetAllocation, overrides: Partial<BudgetAllocation>): BudgetAllocation {
    // validate
    Object.entries(overrides).forEach(([k, v]) => {
        if (!(k in base)) throw new Error(`Invalid stage "${k}"`)
        if (v !== undefined && (v < 0 || v > 100)) throw new Error(`Override for "${k}" must be between 0 and 100`)
    })

    // start with overrides
    const result = { ...base, ...overrides } as BudgetAllocation
    const totalOverride = Object.values(overrides).reduce((sum, v) => sum + (v ?? 0), 0)
    const remainingPct = 100 - totalOverride

    // stages needing adjustment
    const restKeys = Object.keys(base).filter((k) => !(k in overrides)) as (keyof BudgetAllocation)[]
    const sumBaseRest = restKeys.reduce((sum, k) => sum + base[k], 0) || 1

    // re-distribute
    restKeys.forEach((k) => {
        result[k] = (remainingPct / sumBaseRest) * base[k]
    })

    return result
}

interface BudgetDistributionParams {
    budget: number
    objective: string
    audiencesByStage: Record<string, string[]>
    overrides?: Partial<BudgetAllocation>
    stagesToExclude?: string[]
}

interface AudienceDistribution {
    share: number
    amount: number
}

interface StageDistribution {
    share: number
    audiences: Record<string, AudienceDistribution>
}

type BudgetDistributionResult = Record<string, StageDistribution>

/**
 * @param budget                   – total budget (e.g. 1000)
 * @param objective                – one of the keys in OPTIMAL_BUDGET_ALLOCATION
 * @param audiencesByStage
 *             e.g. { acquisition: ['A1','A2'], retargeting:['R1','R2','R3'], … }
 * @param overrides           – optional stage % overrides
 *
 * @returns distribution map:
 * {
 *   acquisition: {
 *     share: 70,               // stage %
 *     audiences: {
 *       A1: { share: 35, amount: 350 },
 *       A2: { share: 35, amount: 350 },
 *     }
 *   },
 *   …
 * }
 */
export function calculateBudgetDistribution({
    budget,
    objective,
    audiencesByStage,
    overrides = {},
    stagesToExclude = [],
}: BudgetDistributionParams): BudgetDistributionResult {
    const base = OPTIMAL_BUDGET_ALLOCATION[objective as CampaignObjective]
    if (!base) throw new Error(`Unknown objective "${objective}"`)

    // 1) remove the excluded stages
    const filteredEntries = Object.entries(base).filter(([stage]) => !stagesToExclude.includes(stage))

    if (filteredEntries.length === 0) {
        throw new Error(`All stages have been excluded`)
    }

    // 2) normalize that filtered base to sum to 100
    const sumFiltered = filteredEntries.reduce((sum, [, pct]) => sum + pct, 0)
    const normalizedBase = Object.fromEntries(
        filteredEntries.map(([stage, pct]) => [stage, sumFiltered > 0 ? (pct / sumFiltered) * 100 : 0]),
    ) as unknown as BudgetAllocation

    // 3) validate overrides against normalizedBase
    const invalid = Object.keys(overrides).filter((k) => !(k in normalizedBase))
    if (invalid.length) {
        throw new Error(`Cannot override excluded stages: ${invalid.join(', ')}`)
    }
    const sumOverrides = Object.values(overrides).reduce((s, v) => s + (v ?? 0), 0)
    if (sumOverrides > 100) {
        throw new Error(`Overrides total ${sumOverrides}%, exceeds 100%`)
    }

    // 4) apply overrides (or just use the normalized base)
    const stagePercents = Object.keys(overrides).length ? adjustPercentages(normalizedBase, overrides) : normalizedBase

    // 5) split each stage's % and budget across its audiences
    const result: BudgetDistributionResult = {}
    for (const [stage, pct] of Object.entries(stagePercents)) {
        const auds = audiencesByStage[stage] || []
        const perAudPct = auds.length ? pct / auds.length : 0
        const perAudAmt = (perAudPct / 100) * budget

        result[stage] = {
            share: Number(pct.toFixed(2)),
            audiences: auds.reduce((map, audId) => {
                map[audId] = {
                    share: Number(perAudPct.toFixed(2)),
                    amount: Number(perAudAmt.toFixed(2)),
                }
                return map
            }, {} as Record<string, AudienceDistribution>),
        }
    }

    return result
}

// Helper function to get available stages for a campaign objective
export const getAvailableStages = (objective?: string): CampaignStage[] => {
    const upperObjective = (objective?.toLowerCase() as CampaignObjective) ?? 'awareness'
    return OBJECTIVE_AVAILABLE_STAGES[upperObjective] || []
}

// Helper function to get all stages as array
export const getAllStages = (): FunnelStageConfig[] => Object.values(FUNNEL_STAGES)

// Helper function to get stage by ID
export const getStageById = (stageId: CampaignStage): FunnelStageConfig | undefined => FUNNEL_STAGES[stageId]

// Helper function to get optimal budget allocation for a campaign objective
export const getOptimalBudgetAllocation = (objective?: string): BudgetAllocation => {
    const upperObjective = (objective?.toLowerCase() as CampaignObjective) ?? 'awareness'
    return OPTIMAL_BUDGET_ALLOCATION[upperObjective] || OPTIMAL_BUDGET_ALLOCATION.awareness
}

export const calculateStagesToExclude = (
    availableStages: CampaignStage[],
    stagesWithoutAudiences: CampaignStage[],
): CampaignStage[] => {
    const allStages: CampaignStage[] = ['acquisition', 'reengagement', 'retargeting', 'retention']
    return allStages.filter((stage) => !availableStages.includes(stage) && !stagesWithoutAudiences.includes(stage))
}

// Helper function to check if audience should be excluded
// The lead form retargeting option is built from the licence's lead forms, and audiences of the ad account exist
// already, so neither needs the pixel
export const isAudienceExcluded = (audience: Audience, pixelIsActive: boolean): boolean => {
    if (pixelIsActive) return false
    if (audience.id === WARM_AUDIENCE_ID || isAccountAudienceId(audience.id)) return false

    const excludedStages = EXCLUDED_AUDIENCES[EXCLUSION_REASONS.invalidPixel] || []
    return excludedStages.includes(audience.funnel_stage)
}

// Helper function to get exclusion reason for tooltip
export const getExclusionReason = (audience: Audience, pixelIsActive: boolean): string[] | null => {
    if (pixelIsActive) return null
    if (audience.id === WARM_AUDIENCE_ID || isAccountAudienceId(audience.id)) return null

    const excludedStages = EXCLUDED_AUDIENCES[EXCLUSION_REASONS.invalidPixel] || []
    if (excludedStages.includes(audience.funnel_stage)) {
        return [
            'This audience cannot be created until your Pixel is active and successfully tracking website traffic.',
            'Please verify that the Pixel is installed correctly. If the installation is new, be aware that it may take some time for visitor data to be processed and appear here. Check again in a few hours.',
        ]
    }

    return null
}

// Helper function to filter audiences based on campaign objective
export const filterAudiencesByObjective = (
    audienceSelections?: AudienceSelections | null,
    objective?: string,
): AudienceSelections => {
    if (!audienceSelections || !objective) return audienceSelections || ({} as AudienceSelections)

    const filtered = { ...audienceSelections }

    // Ensure all stages exist as arrays
    const stages: CampaignStage[] = ['acquisition', 'reengagement', 'retargeting', 'retention']
    stages.forEach((stage) => {
        if (!filtered[stage] || !Array.isArray(filtered[stage])) {
            filtered[stage] = []
        }
    })

    switch (objective.toLowerCase()) {
        case 'awareness':
            // Remove retention, retargeting, reengagement for Awareness
            filtered.retention = []
            filtered.retargeting = []
            filtered.reengagement = []
            break

        case 'traffic':
        case 'engagement':
            // Remove retention for traffic and engagement, others can stay
            filtered.retention = []
            break

        case 'leads':
        case 'app_promotion':
        case 'sales':
        default:
            // Include all audiences for these objectives
            break
    }

    return filtered
}